import React, { useCallback, useEffect, useState, JSX } from 'react';
import { SSOButton } from '../SSOButton';
import { Button } from '../Button';
import { i18n } from '../../i18n/translate';
import { sanitizeUrl } from '@braintree/sanitize-url';
/* @ts-ignore */
import googleLogoImg from '../../images/google_logo.svg';
/* @ts-ignore */
import facebookLogoImg from '../../images/facebook_logo.svg';
import { Flex, Divider, Text } from '@chakra-ui/react';

interface AuthCaps {
  social?: string;
  email?: string;
  username?: string;
  saml?: string;
  google?: boolean;
  facebook?: boolean;
}

interface Props {
  authCaps: AuthCaps;
  authUrls: any;
  idProvidersUrl: string;
  loginText: string;
  onLoginLoad: () => void;
  socialIcon: string;
}

const LoginBox: React.FC<Props> = ({
  authCaps,
  authUrls,
  idProvidersUrl,
  loginText = i18n.t('Login to save your progress'),
  onLoginLoad,
  socialIcon,
}) => {
  const [samlIdProviders, setSamlIdProviders] = useState([]);

  const loadSamlIdProviders = useCallback(async () => {
    if (idProvidersUrl) {
      const idProviders = await fetch(idProvidersUrl).then(res => res.json());
      setSamlIdProviders(idProviders);
    }
  }, [idProvidersUrl]);

  useEffect((): void => {
    if (onLoginLoad) onLoginLoad();
    loadSamlIdProviders();
  }, [loadSamlIdProviders, onLoginLoad]);

  const constructUrl = (authType: string): string => {
    type Entry = {
      auth_type: string;
    };
    return authUrls ? (authUrls.find((entry: Entry) => entry.auth_type === authType) || authUrls[0]).url : '';
  };

  const socialLogin = (): JSX.Element | null => {
    if (!authCaps?.facebook && !authCaps?.google) return null;

    return (
      <>
        {authCaps.google && (
          /* Remove when below is TypeScript-ified */
          /* @ts-ignore */
          <SSOButton
            branded={true}
            href={constructUrl('google')}
            label={i18n.t('Continue with Google')}
            imgUrl={googleLogoImg}
            data={{ action: 'login-with-google' }}
          />
        )}
        {authCaps.facebook && (
          /* Remove when below is TypeScript-ified */
          /* @ts-ignore */
          <SSOButton
            branded={true}
            href={constructUrl('facebook')}
            label={i18n.t('Continue with Facebook')}
            imgUrl={facebookLogoImg}
            icon="facebook"
            iconFamily="fa"
            data={{ action: 'login-with-facebook' }}
          />
        )}
      </>
    );
  };

  const emailLogin = (): JSX.Element | null => {
    if (!authCaps?.email) return null;

    return (
      <Button
        /* @ts-ignore */
        href={constructUrl('email_login')}
        style="primary"
        label={i18n.t('Continue with Email or Username')}
        branded={true}
        data={{ action: 'login-with-email-or-username' }}
        className="w-100"
      />
    );
  };

  const usernameLogin = (): JSX.Element | null => {
    if (!authCaps?.username) return null;

    return (
      <Button
        /* @ts-ignore */
        href={constructUrl('username')}
        style="primary"
        label={i18n.t('Continue with Username')}
        branded={true}
        block={true}
        className="large w-100"
        data={{ action: 'login-with-username' }}
      />
    );
  };

  const canShowSaml = () => authCaps?.saml && samlIdProviders.length > 0;

  const samlLogin = () => {
    if (!canShowSaml()) return null;
    type Provider = {
      id: number;
      auth_url: string;
      display_name: string;
    };
    return samlIdProviders.map((provider: Provider) => {
      const label = i18n.t('Log In with %s Account', { postProcess: 'sprintf', sprintf: [provider.display_name] });
      const authUrl = provider.auth_url.replace('relay_state_value', encodeURIComponent(window.location.href));
      return (
        // @ts-ignore
        <SSOButton
          data={{ action: 'login-with-sso' }}
          branded={true}
          key={provider.id}
          href={sanitizeUrl(authUrl)}
          label={label}
          imgUrl={socialIcon}
        />
      );
    });
  };

  const conditionalOrTag = (): JSX.Element | null => {
    // only display 'or' heading tag when one of the default auth capabilites are active if saml cap active
    if (!authCaps?.email || (!(authCaps?.google || authCaps?.facebook) && !canShowSaml())) return null;

    return (
      <Flex alignItems="center" justifyContent="center">
        <Divider orientation="horizontal" borderColor="gray.300" />
        <Text mx={2}>or</Text>
        <Divider orientation="horizontal" borderColor="gray.300" />
      </Flex>
    );
  };

  const showSsoLogin: string | boolean | undefined = authCaps?.facebook || authCaps?.google || canShowSaml();

  return (
    <>
      <div className="login-title text-center m-b-30">
        {/* Remove when below is TypeScript-ified */
        /* @ts-ignore */}
        <h2 tabIndex="-1">{i18n.t('Log In')}</h2>
        <h4 className="text-muted">{loginText}</h4>
      </div>
      {showSsoLogin && (
        <div className="sso-login">
          {socialLogin()}
          {samlLogin()}
        </div>
      )}
      {conditionalOrTag()}
      {emailLogin()}
      {usernameLogin()}
      {(authCaps?.email || authCaps?.username) && (
        <div className="register-text text-center m-t-20">
          {i18n.t("Don't have an account?")}
          <a className="branded-link m-l-5" href={constructUrl('email_registration')}>
            {i18n.t('Register')}
          </a>
        </div>
      )}
    </>
  );
};

export default LoginBox;
